import { type JSX } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { Header, Footer, PrivacyPolicy } from './components';
import { CreateSecretPage, RetrieveSecretPage, MySecretsPage } from './tools/one-time-secrets';
import { SplitSecretPage, ReconstructSecretPage, SpecificationPage } from './tools/shamir';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Client-side navigation function
export function navigateTo(path: string): void {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function useCurrentRoute(): JSX.Element {
  const pathname = window.location.pathname;

  // Match /secret/:id pattern
  const secretMatch = pathname.match(/^\/secret\/([a-zA-Z0-9-_]+)$/i);
  if (secretMatch !== null && secretMatch[1] !== undefined) {
    const secretId = secretMatch[1];
    return <RetrieveSecretPage secretId={secretId} />;
  }

  // Shamir Secret Sharing routes
  if (pathname === '/shamir/split') {
    return <SplitSecretPage />;
  }

  if (pathname === '/shamir/reconstruct') {
    return <ReconstructSecretPage />;
  }

  if (pathname === '/shamir/specification') {
    return <SpecificationPage />;
  }

  // Privacy policy
  if (pathname === '/privacy') {
    return <PrivacyPolicy />;
  }

  // My Secrets (authenticated users)
  if (pathname === '/my-secrets') {
    return <MySecretsPage />;
  }

  // Default: home page
  return <CreateSecretPage />;
}

export default function App(): JSX.Element {
  const [, forceUpdate] = useState({});

  useEffect(() => {
    const handlePopState = (): void => {
      forceUpdate({}); // Trigger re-render
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const currentPage = useCurrentRoute();

  return (
    <ThemeProvider>
      <AuthProvider>
        <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 [&_*]:transition-colors transition-colors">
          <Header />
          <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-8">
            {currentPage}
          </main>
          <Footer />
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}
